import React, { useEffect, useRef } from "react";

/**
 * PresentationAudio — Phase 5 presentation-only timed audio.
 *
 * Mounted ONLY while Presentation Mode is running. Plays the active step's
 * `audio_url` with a configurable crossfade, loops it while the step lingers,
 * and fades out cleanly on step change (to a step with no audio) or exit.
 *
 * A single <audio> element is reused; when the desired url changes we fade the
 * old track out, swap the src, then fade the new one in. Because the component
 * only mounts inside Presentation Mode, this audio can never leak into normal
 * browsing.
 *
 * Props:
 *   - url    active step audio_url (or null)
 *   - volume 0..1 (default 0.6)
 *   - fade   fade seconds (default 1.2)
 *   - loop   boolean (default true)
 */
export default function PresentationAudio({ url, volume = 0.6, fade = 1.2, loop = true }) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const curUrlRef = useRef("");

  const clearFade = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  const rampTo = (el, to, ms, onDone) => {
    clearFade();
    if (!el) return;
    const from = el.volume;
    const start = performance.now();
    const dur = Math.max(1, ms);
    const tick = (t) => {
      const k = Math.min(1, (t - start) / dur);
      try { el.volume = Math.max(0, Math.min(1, from + (to - from) * k)); } catch (_) {}
      if (k < 1) { rafRef.current = requestAnimationFrame(tick); }
      else { rafRef.current = null; onDone?.(); }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const fadeMs = Math.max(0, Number(fade) || 0) * 1000;
    const target = Math.max(0, Math.min(1, Number(volume) || 0));
    const desired = url || "";

    // No audio for this step → fade current track out (keep element for reuse).
    if (!desired) {
      rampTo(el, 0, fadeMs || 300, () => { try { el.pause(); } catch (_) {} });
      curUrlRef.current = "";
      return;
    }

    // Same track already playing → just retarget volume/loop smoothly.
    if (desired === curUrlRef.current) {
      el.loop = loop !== false;
      rampTo(el, target, 300);
      return;
    }

    // New track → swap src, start muted-ish, fade in.
    const startNew = () => {
      curUrlRef.current = desired;
      el.src = desired;
      el.loop = loop !== false;
      el.volume = 0;
      const p = el.play();
      if (p && p.catch) p.catch(() => { /* autoplay may need a gesture; the
        Start-presentation button IS a gesture so this normally succeeds */ });
      rampTo(el, target, fadeMs || 600);
    };
    // If something is currently audible, fade it out first, then swap.
    if (curUrlRef.current && el.volume > 0.01) {
      rampTo(el, 0, Math.min(fadeMs, 600) || 300, startNew);
    } else {
      startNew();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, volume, fade, loop]);

  // Hard stop on unmount (presentation exit).
  useEffect(() => {
    return () => {
      clearFade();
      const el = audioRef.current;
      if (el) { try { el.pause(); el.src = ""; } catch (_) {} }
    };
  }, []);

  return <audio ref={audioRef} preload="auto" data-testid="presentation-audio" />;
}
